"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	fetchSessionUser,
	mightHaveAuthSession,
	type SessionUser,
} from "@/lib/authClient";
import { subscribeNewsletter } from "@/lib/newsletterPreference";

export default function NewsletterSignup() {
	const [authChecked, setAuthChecked] = useState(false);
	const [user, setUser] = useState<SessionUser | null>(null);
	const [subscribed, setSubscribed] = useState(false);
	const [pending, setPending] = useState(false);

	useEffect(() => {
		let alive = true;

		(async () => {
			if (!mightHaveAuthSession()) {
				if (alive) {
					setUser(null);
					setAuthChecked(true);
				}
				return;
			}

			const sessionUser = await fetchSessionUser({ clearStale: true });
			if (!alive) return;
			setUser(sessionUser);
			setSubscribed(Boolean(sessionUser?.newsletterSubscribed));
			setAuthChecked(true);
		})();

		return () => {
			alive = false;
		};
	}, []);

	const handleSubscribe = async () => {
		if (!user || pending || subscribed) return;

		setPending(true);
		try {
			const result = await subscribeNewsletter();
			if (!result.ok) {
				toast.error(result.error || "Iscrizione non riuscita");
				return;
			}
			setSubscribed(result.subscribed);
			toast.success("Iscrizione alla newsletter effettuata. Controlla la tua email.");
		} catch {
			toast.error("Errore di connessione");
		} finally {
			setPending(false);
		}
	};

	return (
		<div className="w-full space-y-3 pt-2 border-t border-(--card-foreground)/15">
			<h3 className="text-xl font-semibold text-card-foreground flex items-center justify-center lg:justify-start gap-2">
				<Mail className="w-5 h-5 shrink-0" />
				Newsletter
			</h3>

			<p className="text-lg text-foreground/80 leading-relaxed">
				Ricevi aggiornamenti su circolari, eventi e novità della scuola.
			</p>

			{!authChecked ? (
				<p className="text-sm text-muted-foreground">Caricamento…</p>
			) : !user ? (
				<div className="space-y-3">
					<p className="text-base text-muted-foreground">
						L&apos;iscrizione è riservata agli account registrati.
					</p>
					<Button
						asChild
						className="cursor-pointer h-11 bg-(--btn-bg) text-(--btn-foreground) font-semibold text-base rounded-full px-6 shadow-md hover:bg-(--btn-hover) hover:shadow-lg transition-all"
					>
						<Link href="/area-riservata">Accedi per iscriverti</Link>
					</Button>
				</div>
			) : subscribed ? (
				<div className="space-y-2">
					<p className="text-base font-medium text-(--card-foreground)">
						Sei già iscritto alla newsletter.
					</p>
					<p className="text-sm text-muted-foreground">
						Per gestire l&apos;iscrizione vai nell&apos;{" "}
						<Link
							href="/area-riservata"
							className="text-(--card-foreground) underline underline-offset-4 hover:opacity-80"
						>
							area riservata
						</Link>
						.
					</p>
				</div>
			) : (
				<div className="space-y-3">
					<p className="text-base text-muted-foreground break-all">
						Verrà usata l&apos;email{" "}
						<span className="font-semibold text-foreground">{user.email}</span>
					</p>
					<div className="flex flex-wrap gap-3 justify-center lg:justify-start">
						<Button
							type="button"
							disabled={pending}
							onClick={handleSubscribe}
							className="cursor-pointer h-11 bg-(--btn-bg) text-(--btn-foreground) font-semibold text-base rounded-full px-6 shadow-md hover:bg-(--btn-hover) hover:shadow-lg transition-all"
						>
							{pending ? "Attendere…" : "Iscriviti"}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
